<template>
  <div v-if="authorizedAccess()" class="meetings-page mt-2">
    <div class="row" style="padding: 2rem 3rem 0;">
      <div class="col-2">
        <span role="button" :class="currentMeetingView == 'upcoming' ? 'text-primary underlined' : ''" class="tab" @click="currentMeetingView = 'upcoming'">{{ $t('Index.upcoming-meetings') }}</span>
      </div>
      <div class="col-2">
        <span v-if="$auth.hasRole('super-admin')" role="button" :class="currentMeetingView == 'past' ? 'text-primary underlined' : ''" class="tab ms-3" @click="currentMeetingView = 'past'">
          {{ $t('Index.previous-meetings') }}
        </span>
      </div>
      <div class="col-3"></div>
      <div class="col-3" style="text-align: right;">
        <span v-if="$auth.hasRole('super-admin')" role="button" :class="currentMeetingView == 'attendance' ? 'text-primary underlined' : ''" class="tab ms-3" @click="currentMeetingView = 'attendance'">
          {{ $t('Index.attendance-reports') }}
        </span>
      </div>
      <div class="col-2" style="text-align: right;">
        <span v-if="network.can.create_meeting" role="button" :class="currentMeetingView == 'schedule' ? 'text-primary underlined' : ''" class="tab" @click="currentMeetingView = 'schedule'">
          {{ $t('network.meeting.createNewMeeting') }}
        </span>
      </div><hr style="opacity: 0.5; height: 3px;">
    </div>
    
    <div class="row" style="padding: 0 1.5rem;">
      <div v-if="currentMeetingView == 'upcoming'" class="meetings-section">
        <div style="padding: 0 1.7rem 0.3rem;">
          <h6>{{ $t('network.here-you-will-be-able-to-see-all-upcoming-meetings-for') }} <b>{{ network.name }}</b>.
            {{ $t('network.you-can-also-filter-meetings-within-a-given-span-of-time') }}</h6>
          <date-range-picker
            id="upcoming_meeting_selected_date"
            ref="picker"
            v-model="upcoming_meeting_selected_date"
            data-test="upcoming_meeting_selected_date"
            opens="right"
            :ranges="date_ranges"
            :showDropdowns="true"
            :linkedCalendars="false"
            :autoApply="true"
            :locale-data="{ firstDay: 1, format: 'yyyy-mm-dd ' }"
            @update="filteredMeetingsUpcoming"
          >
            <template v-slot:input="picker" style="min-width: 450px;">
              <i class="fas fa-calendar-alt"></i>&nbsp;&nbsp;
              {{ picker.startDate | date }} - {{ picker.endDate | date }}
            </template>
          </date-range-picker>
        </div>
        <table class="table">
          <thead>
            <tr>
              <th scope="col"><i class="fas fa-info-circle me-2"></i>{{ $t('meeting-info') }}</th>
              <th scope="col"><i class="fas fa-calendar me-2"></i>{{ $t('date-and-time') }}</th>
              <th scope="col"><i class="fas fa-repeat me-2"></i>{{ $t('occurrence') }}</th>
              <th scope="col"><i class="fa-solid fa-calendar-circle-plus me-2"></i>{{ $t('network.meeting.addTo') }}</th>
              <th scope="col"><i class="fas fa-share-alt me-2"></i>{{ $t('share') }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- SingleMeeting represents a single tableRow or <tr> -->
            <SingleMeeting
              v-for="(meeting, index) in filteredUpcomingMeetings"
              :key="index"
              :network="network"
              :meeting="meeting"
              :currentMeetingView="currentMeetingView"
              @reload-meeting-list="refreshAllMeetings"
            />

            <tr v-if="filteredUpcomingMeetings.length == 0 && !busy">
              <td colspan="5">
                <div class="alert alert-info mt-2 text-center">
                  {{ $t('network.meeting.noMeetingsFound') }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="busy" class="text-center pt-2">
          <lottie-player
            autoplay
            loop
	          speed="2.5"
            src="/img/animations/custom_loader.json"
            style="height: 130px;"
            >
          </lottie-player>
        </div>
      </div>
      <div v-if="currentMeetingView == 'past'" class="meetings-section">
        <div style="padding: 0 1.7rem 0.3rem;">
          <h6>{{ $t('network.here-you-will-be-able-to-see-all-previous-meetings-for') }} <b>{{ network.name }}</b>.
            {{ $t('network.you-can-also-filter-meetings-within-a-given-span-of-time') }}</h6>
          <date-range-picker
            id="past_meeting_selected_date"
            ref="picker"
            v-model="past_meeting_selected_date"
            data-test="past_meeting_selected_date"
            opens="right"
            :ranges="date_ranges"
            :autoApply="true"
            :showDropdowns="true"
            :linkedCalendars="false"
            :locale-data="{ firstDay: 1, format: 'yyyy-mm-dd ' }"
            @update="filteredMeetingsPast"
          >
            <template v-slot:input="picker" style="min-width: 450px;">
              <i class="fas fa-calendar-alt"></i>&nbsp;&nbsp;
              {{ picker.startDate | date }} - {{ picker.endDate | date }}
            </template>
          </date-range-picker>
        </div>
        <table class="table">
          <thead>
            <tr>
              <th scope="col"><i class="fas fa-info-circle me-2"></i>{{ $t('meeting-info') }}</th>
              <th scope="col"><i class="fas fa-calendar me-2"></i>{{ $t('date-and-time') }}</th>
              <th scope="col"><i class="fas fa-repeat me-2"></i>{{ $t('occurrence') }}</th>
              <th scope="col"><i class="fas fa-file-chart-line me-2"></i>{{ $t('Index.attendance-report') }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- SingleMeeting represents a single tableRow or <tr> -->
            <SingleMeeting
              v-for="(meeting, index) in sortedArray(filteredPastMeetings)"
              :key="index"
              :currentMeetingView="currentMeetingView"
              :meeting="meeting"
              :network="network"
              @reload-meeting-list="refreshAllMeetings"
            />

            <tr v-if="filteredPastMeetings.length == 0 && !busy">
              <td colspan="6">
                <div class="alert alert-info mt-2 text-center">
                  {{ $t('network.meeting.noMeetingsFound') }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="busy" class="text-center pt-2" style="height: 130px;">
          <lottie-player
            autoplay
            loop
	          speed="2.5"
            src="/img/animations/custom_loader.json"
            >
          </lottie-player>
        </div>
      </div>
      <div v-if="currentMeetingView == 'attendance'" class="meetings-section">
        <div class="row">
          <div class="col-md-3" style="text-align: center; padding-top: 2px;">
            <date-range-picker
              id="meeting_attendance_selected_date"
              ref="picker"
              v-model="meeting_attendance_selected_date"
              data-test="meeting_attendance_selected_date"
              opens="right"
              :autoApply="true"
              :ranges="date_ranges"
              :showDropdowns="true"
              :linkedCalendars="false"
              :locale-data="{ firstDay: 1, format: 'yyyy-mm-dd ' }"
              @update="filteredAttendance"
            >
              <template v-slot:input="picker" style="min-width: 450px;">
                <i class="fas fa-calendar-alt"></i>&nbsp;&nbsp;
                {{ picker.startDate | date }} - {{ picker.endDate | date }}
              </template>
            </date-range-picker>
          </div>
          <div class="col-md-7" style="text-align: center;">
            <v-select
              id="selected_meetings"
              v-model="selectedMeetings"
              data-test="selected_meetings"
              :options="sortedArray([...pastMeetingsWithOccurrences, ...inProgressMeetingsWithOccurrences])"
              :placeholder="$t('network.select-meeting-to-see-attendance')"
              :get-option-label="option => option.title + ' ('+ utcToLocal(option.start).format('MMM D, YYYY') +')'"
              multiple
              @input="filterMeetingByTitle"
            >
            <template v-slot:option="option">
              {{ utcToLocal(option.start).format('dddd, MMMM D, YYYY, hh:mm A') }} - <b>{{ option.title }}</b>
            </template>
            </v-select>
          </div>
          <div class="col-md-2" style="text-align: right; padding-top: 2px;">
            <JsonCSV
              :data="filteredAttendanceMeetings"
              :name="network.slug + '-meeting-attendees-list.csv'"
              class="btn btn-primary btn-sm pt-2"
            >
              <i class="fas fa-file-download" style="font-size: 19px;"></i>&nbsp;&nbsp;{{ $t('export-csv') }}
            </JsonCSV>
          </div>
        </div>
        <table class="table attendees">
          <thead>
            <tr>
              <th scope="col">{{ $t('network.date') }}</th>
              <th scope="col">{{ $t('network.auth.firstName') }}</th>
              <th scope="col">{{ $t('network.auth.lastName') }}</th>
              <th scope="col">{{ $t('network.auth.email') }}</th>
              <th scope="col">{{ $t('network.auth.workplace.job_title') }}</th>
              <th scope="col">{{ $t('network.auth.work-place') }}</th>
              <th scope="col">{{ $t('network.auth.workplace_city') }}</th>
              <th scope="col">{{ $t('network.auth.workplace_country') }}</th>
              <th scope="col">{{ $t('network.auth.firstName') }} ({{ $t('curated') }})</th>
              <th scope="col">Preferred First Name ({{ $t('curated') }})</th>
              <th scope="col">Middle Name ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.lastName') }} ({{ $t('curated') }})</th>
              <th scope="col">Full Name ({{ $t('curated') }})</th>
              <th scope="col">Preferred Full Name ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.email') }} 1 ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.email') }} 2 ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.workplace.job_title') }} 1 ({{ $t('curated') }})</th>
              <th scope="col">Department 1 ({{ $t('curated') }})</th>
              <th scope="col">Hospital 1 ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.workplace.job_title') }} 2 ({{ $t('curated') }})</th>
              <th scope="col">Department 2 ({{ $t('curated') }})</th>
              <th scope="col">Hospital 2 ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.work-place') }} ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.workplace_city') }} ({{ $t('curated') }})</th>
              <th scope="col">{{ $t('network.auth.workplace_country') }} ({{ $t('curated') }})</th>
              <th scope="col">Profile Link ({{ $t('curated') }})</th>
              <th scope="col">Tags / Keywords</th>
            </tr>
          </thead>
          <tbody>
            <!-- Attendance represents a single tableRow or <tr> -->
            <Attendance
              v-for="(meeting, index) in filteredAttendanceMeetings"
              :key="index"
              :canStart="true"
              :currentMeetingView="currentMeetingView"
              :network="network"
              :meeting="meeting"
              @reload-meeting-list="refreshAllMeetings"
            />

            <tr v-if="filteredAttendanceMeetings.length == 0 && !busy">
              <td colspan="27">
                <div class="alert alert-info mt-2 text-center">
                  {{ $t('network.member.noResultsToShow') }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="busy" class="text-center pt-2" style="height: 130px;">
          <lottie-player
            autoplay
            loop
	          speed="2.5"
            src="/img/animations/custom_loader.json"
            >
          </lottie-player>
        </div>
      </div>
      <div v-if="currentMeetingView == 'schedule'" class="meetings-section">
        <b-overlay :show="busy">
          <div v-if="$store.getters['isUserIntegratedToZoom']">
            <NetworkMeetingForm :existingForm="form" :timezones="timezones" :update="false" @network-meeting-form-update="sendMeeting" @network-zoom-meeting="sendZoomMeeting" />
          </div>
          <div v-else class="alert alert-warning text-center text-black fw-semibold">
            {{ $t('network.meeting.zoomIntegrationRequired') }}
            <br />
            <router-link v-if="this.$auth.can('zoom.link-account')" to="/profile" class="btn btn-info mt-3 btn-sm">
              {{ $t('network.user.visitProfile') }}
            </router-link>
            <router-link v-else to="/unauthorized-zoom" class="btn btn-info mt-3 btn-sm">
              {{ $t('home.learnMore') }}
            </router-link>
          </div>
        </b-overlay>
      </div>
    </div>
  </div>
</template>
<script>
import Attendance from '@/components/commons/Meetings/Attendance.vue';
import NetworkMeetingForm from '@/components/forms/meeting/NetworkMeetingForm.vue';
import SingleMeeting from '@/components/commons/Meetings/SingleMeeting.vue';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import infiniteScroll from 'vue-infinite-scroll';
import JsonCSV from 'vue-json-csv';
import moment from 'moment';
import orderBy from 'lodash/orderBy';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  components: {
    Attendance,
    NetworkMeetingForm,
    SingleMeeting,
    DateRangePicker,
    JsonCSV,
    vSelect,
  },
  directives: { infiniteScroll },
  filters: {
    date(val) {
      return val ? moment(val).format('YYYY-MM-DD') : '';
    },
  },
  props: {
    network: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      upcomingMeetingsWithOccurrences: [],
      pastMeetingsWithOccurrences: [],
      inProgressMeetingsWithOccurrences: [],
      filteredPastMeetings: [],
      filteredUpcomingMeetings: [],
      filteredAttendanceMeetings: [],
      selectedMeetings: [],
      currentMeetingView: 'upcoming',
      busy: false,
      past_meeting_selected_date: {
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: moment().add(1, 'days').toDate(),
      },
      meeting_attendance_selected_date: {
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: new Date(),
      },
      upcoming_meeting_selected_date: {
        startDate: new Date(new Date().getFullYear(), 0, 1),
        endDate: moment().endOf('year').toDate(),
      },
      date_ranges: {
        //default value for ranges object (if you set this to false ranges will no be rendered)
        Today: [new Date(), new Date()],
        'This week': [moment().startOf('week').toDate(), moment().endOf('week').toDate()],
        'This month': [moment().startOf('month').toDate(), moment().endOf('month').toDate()],
        'This year': [moment().startOf('year').toDate(), moment().endOf('year').toDate()],
        'Next week': [moment().add(1, 'week').startOf('week').toDate(), moment().add(1, 'week').endOf('week').toDate()],
        'Next month': [moment().add(1, 'month').startOf('month').toDate(), moment().add(1, 'month').endOf('month').toDate()],
        'Next year': [moment().add(1, 'year').startOf('year').toDate(), moment().add(1, 'year').endOf('year').toDate()]
      },
      timezones: [],
      form: {
        title: '',
        content: '',
        timezone: '',
        timezone_id: '',
        start_date: null,
        start_time: null,
        duration: 60,
        isRecurring: false,
        recurring: {
          frequency: this.$t('network.meeting.daily'),
          repeatEvery: 1,
          occurs_on: '',
          occurs_on_week: '',
          endDate: '',
        },
        presenter: [],
      },
      zoom_id: '',
    };
  },
  watch: {
    currentMeetingView(newVal) {
      if (newVal == 'attendance') this.filteredAttendance();
    },
  },
  async mounted() {
    await this.authorizedAccess();
    this.$http.get('timezones').then(resp => {
      this.timezones = resp.data;
      let browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
      this.form.timezone = resp.data.find(tz => tz.name == browserTZ);
      if (!this.form.timezone) {
        this.form.timezone = resp.data.find(tz => tz.name == 'UTC');
      }
      this.form.timezone_id = this.form.timezone.id;
    });
    await this.load();
  },
  methods: {
    authorizedAccess() {
      if (this.$auth.auth){
        if (!this.$auth.hasRole('super-admin') && !this.network.is_mine) {
          this.$swal.fire({
            icon: 'error',
            title: this.$t('unauthorized-access'),
          }).then(() => {
            this.$router.push('/networks/' + this.network.slug);
          });
        } else {
          return true;
        }
      } else {
        this.$bus.emit('show-sign-in');
      }
    },
    refreshAllMeetings() {
      this.pastMeetingsWithOccurrences.splice(0);
      this.upcomingMeetingsWithOccurrences.splice(0);
      this.inProgressMeetingsWithOccurrences.splice(0);
      this.filteredPastMeetings.splice(0);
      this.filteredUpcomingMeetings.splice(0);
      this.filteredAttendanceMeetings.splice(0);
      this.load();
    },
    load() {
      this.busy = true;
      this.$http.get('networks/' + this.network.slug + '/meetings').then(resp => {
        resp.data.pastWithOccurrences.forEach(m => this.pastMeetingsWithOccurrences.push(m));
        resp.data.upcomingWithOccurrences.forEach(m => this.upcomingMeetingsWithOccurrences.push(m));
        resp.data.inProgressWithOccurrences.forEach(m => this.inProgressMeetingsWithOccurrences.push(m));
        this.filteredMeetingsUpcoming();
        this.filteredMeetingsPast();
        this.busy = false;
      });
    },
    utcToLocal: function (dt) {
      return moment.utc(dt).local();
    },
    sortedArray(array) {
        return orderBy(array, 'start', 'desc');
    },
    getMeetingShowableText(meeting) {
      meeting = meeting.replace(/<\/?[^>]+(>|$)/g, '');
      return meeting.substr(0, 300);
    },
    sendMeeting(form) {
      this.busy = true;

      this.$http
        .post('networks/' + this.network.slug + '/meetings', form)
        .then(() => {
          this.$router.go();
        })
        .catch(() => {
          this.busy = false;
        });
    },
    sendZoomMeeting(zoom_id, form){
      this.busy = true;
      let data = [];
      data.push({ zoom_id: zoom_id, presenter: form.presenter});
      this.$http.post('networks/' + this.network.slug + '/meetings/external', data)
      .then(resp => {
        if (resp.data.is_recurrent) {
          this.$swal.fire({
            icon: 'info',
            title: 'Should this meeting extend automatically after it ends?',
            footer: '<span style="font-size: smaller;">You can change this preference later by editing the main meeting.</span>',
            showCancelButton: true,
            showCloseButton: true,
            confirmButtonText: this.$t('ResearchCreds.yes'),
            cancelButtonText: this.$t('ResearchCreds.no'),
            customClass: {
              confirmButton: 'btn btn-primary mt-2 mx-3',
              cancelButton: 'btn btn-danger mt-2 mx-3',
              footer: 'border-top-0 text-info',
            },
            buttonsStyling: false,
          }).then((result) => {
            if (result.isConfirmed) {
              this.$http.put(`networks/meetings/autoextend/${resp.data.meeting_id}`).then(() => {
                this.$router.go();
              });
            } else {
              this.$router.go();
            }
          });
        } else {
          this.$router.go();
        }
      })
      .catch(() => {
        this.busy = false;
      });
    },
    filteredMeetingsPast() {
      this.busy = true;
      this.filteredPastMeetings.splice(0);
      let start = moment(this.past_meeting_selected_date.startDate).format('YYYY-MM-DD');
      let end = moment(this.past_meeting_selected_date.endDate).format('YYYY-MM-DD');

      try {
        for (const element of this.pastMeetingsWithOccurrences) {
          if (
            moment(element.start).isSameOrAfter(start, 'day') &&
            moment(element.start).isSameOrBefore(end, 'day')
          ) {
            this.filteredPastMeetings.push(element);
          }
        }

        this.busy = false;
      } catch (error) {
        console.error(error);
        this.busy = false;
      }
    },
    filteredMeetingsUpcoming() {
      this.busy = true;
      this.filteredUpcomingMeetings.splice(0);
      let start = moment(this.upcoming_meeting_selected_date.startDate).format('YYYY-MM-DD');
      let end = moment(this.upcoming_meeting_selected_date.endDate).format('YYYY-MM-DD');

      try {
        for (const element of this.inProgressMeetingsWithOccurrences) {
          if (
            moment(element.start).isSameOrAfter(start, 'day') &&
            moment(element.start).isSameOrBefore(end, 'day')
          ) {
            this.filteredUpcomingMeetings.push(element);
          }
        }

        for (const element of this.upcomingMeetingsWithOccurrences) {
          if (
            moment(element.start).isSameOrAfter(start, 'day') &&
            moment(element.start).isSameOrBefore(end, 'day')
          ) {
            this.filteredUpcomingMeetings.push(element);
          }
        }

        this.busy = false;
      } catch (error) {
        console.error(error);
        this.busy = false;
      }
    },
    async filteredAttendance() {
      this.busy = true;
      this.filteredAttendanceMeetings.splice(0);
      let start = moment(this.meeting_attendance_selected_date.startDate).format('YYYY-MM-DD');
      let end = moment(this.meeting_attendance_selected_date.endDate).format('YYYY-MM-DD');

      try {
        const resp = await this.$http.get('networks/' + this.network.slug + '/meetings');

        for (const element of resp.data.pastWithOccurrences) {
          if (
            moment(element.start).isSameOrAfter(start, 'day') &&
            moment(element.start).isSameOrBefore(end, 'day')
          ) {
            const attendanceResp = await this.$http.post('attendance/' + element.start);
            if (attendanceResp.data != "") {
              attendanceResp.data.forEach(att => {
                this.filteredAttendanceMeetings.push(att);
              });
            }
          }
        }

        this.busy = false;
      } catch (error) {
        console.error(error);
        this.busy = false;
      }
    },
    filterMeetingByTitle(){
      this.busy = true;
      this.filteredAttendanceMeetings.splice(0);
      if(this.selectedMeetings.length == 0){
        this.$http.get('networks/' + this.network.slug + '/meetings').then(resp => {
          resp.data.pastWithOccurrences.forEach(element => {
            this.$http.post('attendance/' + element.start).then(resp => {
              if (resp.data != "") {
                resp.data.forEach(element => {
                  this.filteredAttendanceMeetings.push(element);
                });
              }
            });
          });
        }).then(() => {
          this.busy = false;
        });
      }
      this.selectedMeetings.forEach(element => {
        this.$http.post('attendance/' + element.start).then(resp => {
          if (resp.data != "") {
            resp.data.forEach(element => {
              this.filteredAttendanceMeetings.push(element);
            });
          }
        });
      }).then(() => {
        this.busy = false;
      });
    }
  },
};
</script>
<style scoped>
.table > :not(:last-child) > :last-child > * {
  border-bottom-color: inherit;
}
.meetings-page .nav-pills .nav-item .nav-link {
  border-radius: 10px !important;
  margin-right: 10px;
  border: none;
}
.meetings-page .nav-pills .nav-item .nav-link.active {
  background: none;
  font-weight: bolder;
  color: #926dde;
}
th {
  width: 150px;
  text-align: center;
  color: #5C6D80;
  font-weight: 600;
  font-size: 19px;
  line-height: 30px;
}
.attendees th {
  font-size: 17px;
  line-height: unset;
}
.tab {
  font-weight: 600;
  font-size: 20px;
}
.meetings-section i {
  font-size: 25px;
}
.underlined {
  text-decoration-line: underline;
  text-decoration-thickness: 3px;
  text-underline-offset: 9px;
}
.tab:hover {
  color: #005f9a;
}
.vs__dropdown-toggle {
  border-color: #ccc !important;
}
.vs__selected-options {
  padding: 0px 0px 3px 2px;
}
.meetings-section .reportrange-text {
  height: 45px;
}
</style>